// EMU EOS (E4/E5000/E6400 Ultra) Disk Image Exporter
//
// Creates a disk image (.img) with E4B0 bank directory entries and E3S1
// sample chunks containing 16-bit little-endian PCM.
//
// Disk layout:
//   Block 0 (0x0000):   Empty/zero
//   Block 1-7 (0x0200): E4B0 directory entries (32 bytes each, 32-byte aligned)
//   Block 8+ (0x1000):  Bank data (E3S1 stream chunks with PCM)
//
// E4B0 directory entry (32 bytes):
//   0-15:  Bank name (16 ASCII, space-padded)
//   16-17: unknown
//   18-19: startBlock (u16BE) — bank data offset = startBlock * 512
//   20-21: blockCount (u16BE) — bank data length = blockCount * 512
//   22-27: padding
//   28-31: 'E4B0' marker
//
// E3S1 chunk (within bank data):
//   0-3:   'E3S1' marker
//   4-7:   chunkSize (u32BE) — size of payload (header + PCM)
//   8-9:   unknown (2 bytes, 0x0000)
//   10-25: sample name (16 ASCII, space-padded)
//   26-61: header padding (36 bytes)
//   62-65: sample rate (u32LE) — e.g. 44100
//   66-105: more header fields (40 bytes)
//   106+:  PCM data (16-bit signed LE, mono)
//
// E3S1 header size = 98 bytes (from offset 8 to PCM start)

export const EMU_BLOCK_SIZE = 512;
export const EMU_E3S1_HDR_SIZE = 98;
export const EMU_DIR_OFFSET = 0x0200;   // Directory starts at block 1
export const EMU_DATA_OFFSET = 0x1000;  // Bank data starts at block 8
export const EMU_MAX_BANKS = 1000;

// Storage media sizes
export const EMU_FLOPPY_SIZE = 1474560;       // 1.44 MB floppy (1024 blocks)
export const EMU_DISK_SIZE   = 512 * 1024 * 1024; // 512 MB SCSI hard disk image

// EMU E4 supported sample rates
export const EMU_SAMPLE_RATES = [
   { rate: 44100, label: '44100 Hz (CD quality)' },
   { rate: 48000, label: '48000 Hz' },
   { rate: 32000, label: '32000 Hz' },
   { rate: 22050, label: '22050 Hz' },
   { rate: 16000, label: '16000 Hz' },
];

/**
 * Find the nearest EMU E4 supported sample rate.
 */
export function nearestEmuRate(sr) {
   let best = EMU_SAMPLE_RATES[0];
   let bestDelta = Infinity;
   for (const r of EMU_SAMPLE_RATES) {
      const d = Math.abs(sr - r.rate);
      if (d < bestDelta) { bestDelta = d; best = r; }
   }
   return best.rate;
}

/**
 * Build a 98-byte E3S1 header (payload starts after marker+size).
 *
 * @param {string} name - Sample name (max 16 chars)
 * @param {number} sampleRate - Sample rate in Hz
 * @returns {Uint8Array} 98-byte header
 */
function buildE3s1Header(name, sampleRate) {
   const h = new Uint8Array(EMU_E3S1_HDR_SIZE);
   const dv = new DataView(h.buffer);

   // Offset 0: unknown (2 bytes)
   h[0] = 0x00;
   h[1] = 0x00;

   // Offset 2: sample name (16 ASCII, space-padded)
   const nameBytes = new TextEncoder().encode(name.substring(0, 16));
   for (let i = 0; i < 16; i++) {
      h[2 + i] = i < nameBytes.length ? nameBytes[i] : 0x20;
   }

   // Offset 18: padding (36 bytes, zeros)
   // Offset 54: sample rate (u32LE)
   dv.setUint32(54, sampleRate, true);

   // Offset 58-97: more header fields (zeros)

   return h;
}

/**
 * Build an E3S1 chunk (marker + size + header + PCM).
 *
 * @param {string} name - Sample name
 * @param {Int16Array} pcm - 16-bit signed PCM (mono)
 * @param {number} sampleRate - Sample rate in Hz
 * @returns {Uint8Array} E3S1 chunk data
 */
function buildE3s1Chunk(name, pcm, sampleRate) {
   const header = buildE3s1Header(name, sampleRate);
   const pcmBytes = pcm.length * 2;
   const chunkSize = EMU_E3S1_HDR_SIZE + pcmBytes; // payload size

   // Total: 4 (marker) + 4 (size) + chunkSize
   const chunk = new Uint8Array(8 + chunkSize);
   const dv = new DataView(chunk.buffer);

   // Marker 'E3S1'
   chunk[0] = 0x45; // 'E'
   chunk[1] = 0x33; // '3'
   chunk[2] = 0x53; // 'S'
   chunk[3] = 0x31; // '1'

   // Chunk size (u32BE)
   dv.setUint32(4, chunkSize, false);

   // Header (98 bytes)
   chunk.set(header, 8);

   // PCM data (16-bit signed LE)
   for (let i = 0; i < pcm.length; i++) {
      dv.setInt16(8 + EMU_E3S1_HDR_SIZE + i * 2, pcm[i], true);
   }

   return chunk;
}

/**
 * Build an E4B0 directory entry (32 bytes).
 *
 * @param {string} bankName - Bank name (max 16 chars)
 * @param {number} startBlock - Start block number (bank data offset = startBlock * 512)
 * @param {number} blockCount - Block count (bank data length = blockCount * 512)
 * @returns {Uint8Array} 32-byte directory entry
 */
function buildE4b0Entry(bankName, startBlock, blockCount) {
   const entry = new Uint8Array(32);
   const dv = new DataView(entry.buffer);

   // Bank name (16 ASCII, space-padded)
   const nameBytes = new TextEncoder().encode(bankName.substring(0, 16));
   for (let i = 0; i < 16; i++) {
      entry[i] = i < nameBytes.length ? nameBytes[i] : 0x20;
   }

   // Offset 16-17: unknown
   entry[16] = 0x00;
   entry[17] = 0x00;

   // Offset 18-19: startBlock (u16BE)
   dv.setUint16(18, startBlock, false);

   // Offset 20-21: blockCount (u16BE)
   dv.setUint16(20, blockCount, false);

   // Offset 22-27: padding
   // Offset 28-31: 'E4B0' marker
   entry[28] = 0x45; // 'E'
   entry[29] = 0x34; // '4'
   entry[30] = 0x42; // 'B'
   entry[31] = 0x30; // '0'

   return entry;
}

/**
 * Calculate the disk size needed for a set of samples.
 *
 * @param {Array} samples - Array of { pcm: Int16Array }
 * @returns {number} - disk size in bytes
 */
export function calcEmuCapacity(samples) {
   let totalBankBytes = 0;
   for (const smp of samples) {
      if (!smp.pcm || smp.pcm.length === 0) continue;
      const pcmBytes = smp.pcm.length * 2;
      const chunkSize = 8 + EMU_E3S1_HDR_SIZE + pcmBytes; // marker + size + header + PCM
      totalBankBytes += chunkSize;
   }
   // Pad each bank to block boundary
   const totalBankBlocks = Math.ceil(totalBankBytes / EMU_BLOCK_SIZE);
   const totalBankSize = totalBankBlocks * EMU_BLOCK_SIZE;
   return EMU_DATA_OFFSET + totalBankSize;
}

/**
 * Build a complete EMU E4 disk image containing multiple samples.
 *
 * Each sample becomes an E3S1 chunk within a single bank. The bank is
 * registered in the E4B0 directory.
 *
 * @param {Array} samples - Array of { name, pcm: Int16Array, sampleRate }
 * @param {object} opts - { name: bankName, media: 'floppy'|'disk' }
 * @returns {Uint8Array} Disk image data
 */
export function buildEmuDisk(samples, opts = {}) {
   const bankName = (opts.name || 'EMU BANK').substring(0, 16).padEnd(16, ' ');
   const media = opts.media || 'floppy';
   const mediaSize = media === 'disk' ? EMU_DISK_SIZE : EMU_FLOPPY_SIZE;

   // Build all E3S1 chunks
   const chunks = [];
   let totalChunkBytes = 0;
   for (const smp of samples) {
      if (!smp.pcm || smp.pcm.length === 0) continue;
      const name = (smp.name || `Sample ${chunks.length + 1}`).substring(0, 16);
      const sr = smp.sampleRate || 44100;
      const chunk = buildE3s1Chunk(name, smp.pcm, sr);
      chunks.push(chunk);
      totalChunkBytes += chunk.length;
   }

   if (chunks.length === 0) {
      return new Uint8Array(mediaSize);
   }

   // Calculate bank size (padded to block boundary)
   const bankBlocks = Math.ceil(totalChunkBytes / EMU_BLOCK_SIZE);
   const bankSize = bankBlocks * EMU_BLOCK_SIZE;
   const startBlock = EMU_DATA_OFFSET / EMU_BLOCK_SIZE; // block 8

   // Calculate total disk size — pad to media size, or grow if needed (disk mode only)
   const minNeeded = EMU_DATA_OFFSET + bankSize;
   const diskSize = media === 'floppy'
      ? Math.max(EMU_FLOPPY_SIZE, minNeeded)  // floppy: fixed 1.44 MB
      : Math.max(EMU_DISK_SIZE, minNeeded);   // disk: 512 MB or larger if needed
   const disk = new Uint8Array(diskSize);
   const dv = new DataView(disk.buffer);

   // Write EMU E4 volume header at offset 0
   // detectEmuDisk() checks for a non-empty volume name at offset 0 (16 bytes)
   // and totalBlocks at offset 0x10 (u32LE)
   const volName = bankName.substring(0, 16);
   const volBytes = new TextEncoder().encode(volName);
   for (let i = 0; i < 16; i++) {
      disk[i] = i < volBytes.length ? volBytes[i] : 0x20;
   }
   dv.setUint32(0x10, Math.floor(diskSize / EMU_BLOCK_SIZE), true);

   // Write E4B0 directory entry at EMU_DIR_OFFSET (block 1)
   const dirEntry = buildE4b0Entry(bankName.trim(), startBlock, bankBlocks);
   disk.set(dirEntry, EMU_DIR_OFFSET);

   // Write bank data at EMU_DATA_OFFSET (block 8)
   let bankOffset = EMU_DATA_OFFSET;
   for (const chunk of chunks) {
      disk.set(chunk, bankOffset);
      bankOffset += chunk.length;
   }

   return disk;
}

/**
 * Build a standalone E4B bank file (IFF-style: FORM + E4B0).
 *
 * Layout:
 *   FORM + size + E4B0
 *     TOC1 chunk  — table of contents (E4Ma reference, 32-byte entries)
 *     E4Ma chunk   — 24-byte header + 32-byte E3S1 sample entries
 *     E3S1 chunks  — 98-byte header + 16-bit LE PCM per sample
 *
 * @param {Array} samples - Array of { name, pcm: Int16Array, sampleRate }
 * @param {object} opts - { name: bankName }
 * @returns {Uint8Array} E4B file data
 */
export function buildEmuE4b(samples, opts = {}) {
   const bankName = (opts.name || 'EMU BANK').substring(0, 16);

   // Build E3S1 chunks for each sample
   const e3s1Chunks = [];
   for (const smp of samples) {
      if (!smp.pcm || smp.pcm.length === 0) continue;
      const name = (smp.name || `Sample ${e3s1Chunks.length + 1}`).substring(0, 16);
      const sr = smp.sampleRate || 44100;
      e3s1Chunks.push(buildE3s1Chunk(name, smp.pcm, sr));
   }

   if (e3s1Chunks.length === 0) {
      // Return minimal empty E4B
      const empty = new Uint8Array(12 + 8 + 32); // FORM + TOC1 (minimal)
      empty[0] = 0x46; empty[1] = 0x4f; empty[2] = 0x52; empty[3] = 0x4d; // FORM
      const dv = new DataView(empty.buffer);
      dv.setUint32(4, 8 + 8 + 32, false); // form size
      empty[8] = 0x45; empty[9] = 0x34; empty[10] = 0x42; empty[11] = 0x30; // E4B0
      return empty;
   }

   // Calculate offsets:
   // FORM header: 12 bytes (FORM + size + E4B0)
   // TOC1 chunk: 8 + 32 = 40 bytes (one E4Ma entry)
   // E4Ma chunk: 8 + 24 + (32 * N) bytes (header + N sample entries)
   // E3S1 chunks: variable

   const toc1PayloadSize = 32; // single E4Ma reference entry
   const e4maHeaderSize = 24;
   const e4maTocSize = 32 * e3s1Chunks.length;
   const e4maPayloadSize = e4maHeaderSize + e4maTocSize;

   const toc1Offset = 12;                    // after FORM header
   const e4maOffset = toc1Offset + 8 + toc1PayloadSize; // after TOC1 chunk
   const e3s1StartOffset = e4maOffset + 8 + e4maPayloadSize; // after E4Ma chunk

   // Calculate PCM offsets for each E3S1 chunk (relative to file start)
   const pcmOffsets = [];
   let runningOffset = e3s1StartOffset;
   for (const chunk of e3s1Chunks) {
      pcmOffsets.push(runningOffset + 8 + EMU_E3S1_HDR_SIZE); // PCM starts after marker+size+header
      runningOffset += chunk.length;
   }

   // Build E4Ma TOC entries (32 bytes each)
   const e4maTocEntries = new Uint8Array(e4maTocSize);
   const e4maDv = new DataView(e4maTocEntries.buffer);
   for (let i = 0; i < e3s1Chunks.length; i++) {
      const tp = i * 32;
      const smp = samples[i];
      const name = (smp.name || `Sample ${i + 1}`).substring(0, 14);

      // Marker 'E3S1'
      e4maTocEntries[tp] = 0x45; e4maTocEntries[tp + 1] = 0x33;
      e4maTocEntries[tp + 2] = 0x53; e4maTocEntries[tp + 3] = 0x31;

      // Bytes 4-7: unknown (zero)
      // Bytes 8-11: PCM offset (u32BE)
      e4maDv.setUint32(tp + 8, pcmOffsets[i], false);

      // Bytes 12-13: unknown (zero)
      // Bytes 14-27: sample name (14 ASCII, space-padded)
      const nameBytes = new TextEncoder().encode(name);
      for (let j = 0; j < 14; j++) {
         e4maTocEntries[tp + 14 + j] = j < nameBytes.length ? nameBytes[j] : 0x20;
      }
      // Bytes 28-31: unknown (zero)
   }

   // Build E4Ma chunk (marker + size + 24-byte header + TOC entries)
   const e4maChunk = new Uint8Array(8 + e4maPayloadSize);
   const e4maChunkDv = new DataView(e4maChunk.buffer);
   e4maChunk[0] = 0x45; e4maChunk[1] = 0x34; e4maChunk[2] = 0x4d; e4maChunk[3] = 0x61; // 'E4Ma'
   e4maChunkDv.setUint32(4, e4maPayloadSize, false); // size (BE)
   // 24-byte header: zeros (could hold bank metadata but not required for parsing)
   e4maChunk.set(e4maTocEntries, 8 + e4maHeaderSize);

   // Build TOC1 chunk (marker + size + 32-byte E4Ma reference)
   const toc1Chunk = new Uint8Array(8 + toc1PayloadSize);
   const toc1Dv = new DataView(toc1Chunk.buffer);
   toc1Chunk[0] = 0x54; toc1Chunk[1] = 0x4f; toc1Chunk[2] = 0x43; toc1Chunk[3] = 0x31; // 'TOC1'
   toc1Dv.setUint32(4, toc1PayloadSize, false); // size (BE)
   // TOC1 entry: marker(4) + size(4) + offset(4) + name(20)
   toc1Chunk[8] = 0x45; toc1Chunk[9] = 0x34; toc1Chunk[10] = 0x4d; toc1Chunk[11] = 0x61; // 'E4Ma'
   toc1Dv.setUint32(12, e4maPayloadSize, false); // E4Ma size
   toc1Dv.setUint32(16, e4maOffset, false);      // E4Ma offset in file
   const bankBytes = new TextEncoder().encode(bankName);
   for (let i = 0; i < 20; i++) {
      toc1Chunk[20 + i] = i < bankBytes.length ? bankBytes[i] : 0x20;
   }

   // Calculate total file size
   let totalSize = 12; // FORM header
   totalSize += toc1Chunk.length;
   totalSize += e4maChunk.length;
   for (const chunk of e3s1Chunks) totalSize += chunk.length;

   // Build the file
   const file = new Uint8Array(totalSize);
   const fileDv = new DataView(file.buffer);

   // FORM header
   file[0] = 0x46; file[1] = 0x4f; file[2] = 0x52; file[3] = 0x4d; // 'FORM'
   fileDv.setUint32(4, totalSize - 8, false); // FORM size (everything after FORM+size)
   file[8] = 0x45; file[9] = 0x34; file[10] = 0x42; file[11] = 0x30; // 'E4B0'

   // Write TOC1
   file.set(toc1Chunk, 12);

   // Write E4Ma
   file.set(e4maChunk, e4maOffset);

   // Write E3S1 chunks
   let writeOffset = e3s1StartOffset;
   for (const chunk of e3s1Chunks) {
      file.set(chunk, writeOffset);
      writeOffset += chunk.length;
   }

   return file;
}
